import React, { useEffect, useState, useCallback } from 'react';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * LanguageSwitcher — translates the whole site via Google Translate widget.
 *
 * Strategy:
 *  - Inject the official Google Translate Element script once (idempotent)
 *  - Hide the default Google UI via global CSS
 *  - Drive the language change by setting the `googtrans` cookie + reload
 *    (most reliable cross-environment method; safer than poking the hidden
 *    <select> which Google occasionally rewrites)
 */

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' }, // original — resets translation
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

const GTRANS_COOKIE = 'googtrans';

const setCrossDomainCookie = (name, value) => {
  // Google Translate looks for the cookie on both the current host and its
  // parent domain — we set both for maximum compatibility.
  const host = window.location.hostname;
  document.cookie = `${name}=${value};path=/`;
  if (host.split('.').length > 1) {
    const parent = '.' + host.split('.').slice(-2).join('.');
    document.cookie = `${name}=${value};path=/;domain=${parent}`;
  }
};

const readGoogtrans = () => {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!m) return 'fr';
  // Cookie format: "/fr/en" or "/auto/en"
  const parts = decodeURIComponent(m[1]).split('/').filter(Boolean);
  return parts[1] || 'fr';
};

const LanguageSwitcher = () => {
  const [current, setCurrent] = useState('fr');

  useEffect(() => {
    setCurrent(readGoogtrans());

    if (window.__googleTranslateInjected) return;
    window.__googleTranslateInjected = true;

    // Hidden container required by the script
    if (!document.getElementById('google_translate_element')) {
      const div = document.createElement('div');
      div.id = 'google_translate_element';
      div.style.position = 'fixed';
      div.style.top = '-9999px';
      div.style.left = '-9999px';
      document.body.appendChild(div);
    }

    // Init callback exposed globally
    window.googleTranslateElementInit = () => {
      try {
        // eslint-disable-next-line no-undef, new-cap
        new google.translate.TranslateElement(
          {
            pageLanguage: 'fr',
            includedLanguages: LANGUAGES.map((l) => l.code).join(','),
            autoDisplay: false,
            layout: window.google?.translate?.TranslateElement?.InlineLayout?.SIMPLE,
          },
          'google_translate_element',
        );
      } catch (err) {
        // Swallow — translation is non-critical
        console.warn('Google Translate init failed', err);
      }
    };

    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const handleSelect = useCallback((code) => {
    setCurrent(code);
    if (code === 'fr') {
      // Reset to original: clear cookie and reload
      setCrossDomainCookie(GTRANS_COOKIE, '/fr/fr');
      // Also explicitly clear the cross-domain cookie if any
      document.cookie = 'googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    } else {
      setCrossDomainCookie(GTRANS_COOKIE, `/fr/${code}`);
    }
    // Reload so Google Translate applies the new language to the whole page
    window.location.reload();
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-[#64748B] hover:bg-[#F8FAFC] hover:text-[#2D3E50] transition-colors notranslate"
          translate="no"
          aria-label="Change language"
          data-testid="language-switcher-trigger"
        >
          <span className="text-base leading-none" aria-hidden="true">{currentLang.flag}</span>
          <span className="hidden sm:inline text-xs uppercase tracking-wider font-medium">
            {currentLang.code === 'zh-CN' ? 'ZH' : currentLang.code.toUpperCase()}
          </span>
          <Globe className="h-3.5 w-3.5 text-[#94A3B8] sm:ml-0.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 max-h-[70vh] overflow-y-auto notranslate"
        translate="no"
      >
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">
            Choose a language
          </p>
          <p className="text-xs text-[#64748B] mt-0.5">
            Powered by Google Translate
          </p>
        </div>
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => handleSelect(lang.code)}
            className={`flex items-center gap-3 cursor-pointer ${
              current === lang.code ? 'bg-[#FFF5ED] text-[#E8945A] font-medium' : ''
            }`}
            data-testid={`lang-${lang.code}`}
          >
            <span className="text-lg leading-none" aria-hidden="true">{lang.flag}</span>
            <span className="flex-1 text-sm">{lang.label}</span>
            {current === lang.code && <Check className="h-3.5 w-3.5 text-[#E8945A]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
